import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import GoogleMap from 'components/GoogleMaps/GoogleMap';
import GoogleMarker from 'components/GoogleMaps/GoogleMarker';
import { DispositivelModel, DispositivoLog, EntidadeModel, PoliticaModel, Regional } from 'models/entityCore';
import { fDateSimple } from 'utils/formatTime';
import axios from 'utils/axios';
import Modal, { BotaoSecundario } from 'components/gps/Modal';
import { Num } from 'components/gps';

/* ============================================================================
   ModalDispositivoInfo - o que a Busca abre ao clicar num resultado.

   Migrado do Dialog do MUI para o Modal do visual novo. A leitura da resposta NAO
   mudou: o endpoint /dispositivos/dispositivo-info devolve nomes diferentes para a
   mesma coisa (grupo|entidade, politica|policy, logDevice|ultimoLog|
   ultimoLogAcesso|lastLog), e os `useMemo` de normalizacao continuam iguais -
   mexer neles seria trocar o problema de lugar sem ganhar nada.

   DUAS MUDANCAS DE COMPORTAMENTO, as duas deliberadas:

   1. O MARCADOR NAO E MAIS ARRASTAVEL. Estava `draggable` com `setLat`/`setLng`/
      `setAddress`: dava para arrastar o pino e a tela passava a exibir
      coordenadas e endereco que o aparelho nunca reportou - inventando
      localizacao numa tela de consulta. Mesmo defeito que existia na
      geolocalizacao do dispositivo, corrigido lá.

   2. As coordenadas aparecem com o horario do log ao lado. Latitude e longitude
      sem QUANDO nao dizem onde o aparelho esta, dizem onde ele estava em algum
      momento - e essa diferenca e o que faz alguem ir buscar o aparelho no lugar
      errado.

   O componente do mapa (GoogleMap/GoogleMarker) fica como esta: e integracao com
   Google e nao entra na migracao de visual.
   ============================================================================ */

type DeviceInfoResponse = {
  device?: DispositivelModel;
  dispositivo?: DispositivelModel;
  regional?: Regional | string;
  grupo?: EntidadeModel | string;
  entidade?: EntidadeModel | string;
  politica?: PoliticaModel | string;
  policy?: PoliticaModel | string;
  logDevice?: Partial<DispositivoLog>;
  ultimoLog?: Partial<DispositivoLog>;
  ultimoLogAcesso?: Partial<DispositivoLog>;
  lastLog?: Partial<DispositivoLog>;
  latitude?: string | number;
  longitude?: string | number;
  endereco?: string;
  nome?: string;
  deviceId?: string;
};

function getTextValue(value: unknown) {
  if (!value) return ' - ';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return ' - ';
}

function getLogInfo(data: DeviceInfoResponse | null) {
  if (!data) return null;

  return (
    data.logDevice ||
    data.ultimoLog ||
    data.ultimoLogAcesso ||
    data.lastLog || {
      latitude: data.latitude as string,
      longitude: data.longitude as string,
      endereco: data.endereco,
    }
  );
}

/** Linha rotulo/valor. O rotulo em cima, pequeno; o valor em baixo, legivel. */
function Linha({ rotulo, children, alerta }: { rotulo: string; children: React.ReactNode; alerta?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.07em] text-faint">{rotulo}</div>
      <div className={`text-[12.5px] ${alerta ? 'font-bold text-danger' : 'text-ink'}`}>{children}</div>
    </div>
  );
}

export default function ModalDispositivoInfo({
  open,
  setOpen,
  device,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  device: DispositivelModel;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfoResponse | null>(null);
  const [zoom, setZoom] = useState(16);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [address, setAddress] = useState('');

  useEffect(() => {
    if (!open || !device?.deviceId) return;

    setIsLoading(true);
    axios
      .get<DeviceInfoResponse>(`/dispositivos/dispositivo-info?deviceId=${encodeURIComponent(device.deviceId)}`)
      .then((response) => {
        const data = response.data || {};
        const log = getLogInfo(data);

        setDeviceInfo(data);
        setAddress(log?.endereco || '');

        const nextLat = log?.latitude ? Number(log.latitude) : null;
        const nextLng = log?.longitude ? Number(log.longitude) : null;
        setLat(Number.isFinite(nextLat) ? nextLat : null);
        setLng(Number.isFinite(nextLng) ? nextLng : null);
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errors?.[0]?.message ||
          error?.response?.data?.message ||
          'Ops ocorreu um erro ao carregar as informações do dispositivo.';

        enqueueSnackbar(message, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
        setDeviceInfo(null);
        setLat(null);
        setLng(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [device?.deviceId, enqueueSnackbar, open]);

  const normalizedDevice = useMemo(
    () => deviceInfo?.device || deviceInfo?.dispositivo || device,
    [device, deviceInfo]
  );

  const normalizedGroup = useMemo(() => {
    if (typeof deviceInfo?.grupo === 'object') return deviceInfo.grupo;
    if (typeof deviceInfo?.entidade === 'object') return deviceInfo.entidade;
    return normalizedDevice?.entidade;
  }, [deviceInfo?.entidade, deviceInfo?.grupo, normalizedDevice?.entidade]);

  const normalizedPolicy = useMemo(() => {
    if (typeof deviceInfo?.politica === 'object') return deviceInfo.politica;
    if (typeof deviceInfo?.policy === 'object') return deviceInfo.policy;
    return normalizedGroup?.politicas?.[0];
  }, [deviceInfo?.politica, deviceInfo?.policy, normalizedGroup?.politicas]);

  const regionalName = useMemo(() => {
    if (typeof deviceInfo?.regional === 'string') return deviceInfo.regional;
    if (typeof deviceInfo?.regional === 'object') return deviceInfo.regional?.nome;
    if (normalizedGroup?.regional?.nome) return normalizedGroup.regional.nome;
    return normalizedPolicy?.regional?.nome || ' - ';
  }, [deviceInfo?.regional, normalizedGroup?.regional?.nome, normalizedPolicy?.regional?.nome]);

  const grupoNome = useMemo(() => {
    if (typeof deviceInfo?.grupo === 'string') return deviceInfo.grupo;
    if (typeof deviceInfo?.entidade === 'string') return deviceInfo.entidade;
    return normalizedGroup?.nome || normalizedDevice?.grupoNome || ' - ';
  }, [deviceInfo?.entidade, deviceInfo?.grupo, normalizedDevice?.grupoNome, normalizedGroup?.nome]);

  const politicaNome = useMemo(() => {
    if (typeof deviceInfo?.politica === 'string') return deviceInfo.politica;
    if (typeof deviceInfo?.policy === 'string') return deviceInfo.policy;
    return normalizedPolicy?.name || normalizedPolicy?.nome || normalizedDevice?.policyName || ' - ';
  }, [
    deviceInfo?.politica,
    deviceInfo?.policy,
    normalizedDevice?.policyName,
    normalizedPolicy?.name,
    normalizedPolicy?.nome,
  ]);

  const logInfo = useMemo(() => getLogInfo(deviceInfo), [deviceInfo]);
  const foiDeletado = Boolean(normalizedDevice?.deletedAt);
  const quandoLog = logInfo?.createdAt ? fDateSimple(logInfo.createdAt) : null;

  return (
    <Modal
      aberto={open}
      titulo={normalizedDevice?.nome || deviceInfo?.nome || 'Dispositivo'}
      descricao={
        <span className="tabular-nums">
          {normalizedDevice?.deviceId || deviceInfo?.deviceId || device?.deviceId || ' - '}
        </span>
      }
      largura="max-w-[900px]"
      onFechar={() => setOpen(false)}
      rodape={<BotaoSecundario onClick={() => setOpen(false)}>Fechar</BotaoSecundario>}
    >
      {isLoading ? (
        <div className="py-16 text-center text-[12.5px] text-muted-foreground">Carregando informações...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            {foiDeletado && (
              <div className="rounded-lg border border-danger bg-danger-soft p-2.5 text-[12px] font-bold text-danger">
                Este dispositivo foi deletado.
              </div>
            )}

            {/* MAIS FICHA, MENOS IDA E VOLTA (10/09/2026, a pedido).

                A coluna trazia cinco linhas - regional, grupo, politica, log e
                endereco - e todo o resto exigia sair da Busca e abrir o detalhe
                completo. Mas a Busca e a tela do atendimento: a pessoa esta ao
                telefone, procurou por IMEI ou serial e precisa CONFERIR o
                aparelho ("e um moto g05? o serial bate? qual a versao do hub?").
                Cada campo que falta aqui e uma navegacao no meio da ligacao - e
                o detalhe completo nem existe para aparelho deletado, que e
                justamente quando mais se pergunta.

                Os dados ja vinham na resposta e nao eram exibidos: nao ha
                requisicao nova. Campo vazio some da lista em vez de virar uma
                linha com travessao - dez travessoes empilhados escondem os tres
                valores que importam. */}
            <Linha rotulo="Regional">{regionalName}</Linha>
            <Linha rotulo="Grupo">{grupoNome}</Linha>
            <Linha rotulo="Política">{politicaNome}</Linha>

            {(normalizedDevice?.hardwareInfo_manufacturer || normalizedDevice?.hardwareInfo_model) && (
              <Linha rotulo="Aparelho">
                {[normalizedDevice?.hardwareInfo_manufacturer, normalizedDevice?.hardwareInfo_model]
                  .filter(Boolean)
                  .join(' ')}
              </Linha>
            )}
            {normalizedDevice?.hardwareInfo_serialNumber && (
              <Linha rotulo="Serial">
                <Num>{normalizedDevice.hardwareInfo_serialNumber}</Num>
              </Linha>
            )}
            {normalizedDevice?.networkInfo_imei && (
              <Linha rotulo="IMEI">
                <Num>{normalizedDevice.networkInfo_imei}</Num>
              </Linha>
            )}
            {normalizedDevice?.cpf && (
              <Linha rotulo="CPF">
                <Num>{normalizedDevice.cpf}</Num>
              </Linha>
            )}
            {normalizedDevice?.softwareInfo_androidVersion && (
              <Linha rotulo="Android">{normalizedDevice.softwareInfo_androidVersion}</Linha>
            )}
            {normalizedDevice?.versaoHub && (
              <Linha rotulo="Versão do Hub">
                <Num>{normalizedDevice.versaoHub}</Num>
              </Linha>
            )}
            {logInfo?.bateria != null && (
              <Linha rotulo="Bateria">
                {/* Vermelho abaixo de 20%: numa consulta de atendimento a
                    bateria explica metade das perguntas ("nao aparece no
                    sistema"), e um numero cinza no meio de dez linhas nao e
                    lido. */}
                <Num className={Number(logInfo.bateria) <= 20 ? 'font-bold text-danger' : undefined}>
                  {logInfo.bateria}%
                </Num>
              </Linha>
            )}
            {logInfo?.wifi != null && <Linha rotulo="WIFI">{logInfo.wifi ? 'Sim' : 'Não'}</Linha>}

            <Linha rotulo="Último log">{quandoLog || ' - '}</Linha>
            <Linha rotulo="Endereço">{getTextValue(logInfo?.endereco || address)}</Linha>
            <Linha rotulo="Coordenadas">
              {lat !== null && lng !== null ? (
                <>
                  <Num>
                    {lat}, {lng}
                  </Num>
                  {/* O quando ao lado do onde: sem isso a coordenada parece a
                      posicao atual do aparelho, e ela e a do ultimo log. */}
                  {quandoLog && <span className="ml-2 text-[11px] text-faint">em {quandoLog}</span>}
                </>
              ) : (
                ' - '
              )}
            </Linha>
          </div>

          <div>
            {lat === null || lng === null ? (
              <div className="rounded-lg border border-warn bg-warn-soft p-3 text-[12px]">
                Esse dispositivo não possui última localização disponível.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-line">
                <GoogleMap
                  center={{ lat, lng }}
                  zoom={zoom}
                  setZoom={setZoom}
                  style={{ width: '100%', height: '360px' }}
                  disableDefaultUI
                  clickableIcons={false}
                >
                  {/* Sem `draggable`: arrastar o pino reescrevia lat/lng/endereco
                      na tela e passava a mostrar um lugar onde o aparelho nunca
                      esteve. Os setters continuam sendo passados porque o
                      GoogleMarker os declara obrigatorios no tipo; sem `draggable`
                      ele nunca chama nenhum deles. */}
                  <GoogleMarker
                    position={{ lat, lng }}
                    setLat={setLat as any}
                    setLng={setLng as any}
                    setAddress={setAddress}
                  />
                </GoogleMap>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
